import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { clinicRecipient, financialROI } from "@/mocks/demo-flow";

export default function ClinicSignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [step, setStep] = useState<"form" | "processing" | "done">("form");
  const [baaSigned, setBaaSigned] = useState(false);
  const [formData, setFormData] = useState({
    clinicName: clinicRecipient.name,
    leadProsthetist: clinicRecipient.leadProsthetist,
    email: "james.mcpherson@tylerprosthetics.com",
    phone: clinicRecipient.phone,
    address: clinicRecipient.address,
    password: "",
    confirmPassword: "",
  });
  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: "",
  });
  const [passwordError, setPasswordError] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (
      (field === "password" || field === "confirmPassword") &&
      passwordError
    ) {
      setPasswordError("");
    }
  };

  const handleCardChange = (field: string, value: string) => {
    setCardData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit =
    baaSigned &&
    formData.clinicName.trim() &&
    formData.leadProsthetist.trim() &&
    formData.email.trim() &&
    formData.phone.trim() &&
    formData.address.trim() &&
    formData.password.trim() &&
    formData.confirmPassword.trim() &&
    formData.password === formData.confirmPassword &&
    cardData.number.trim() &&
    cardData.expiry.trim() &&
    cardData.cvc.trim() &&
    cardData.name.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    setPasswordError("");
    setStep("processing");

    try {
      await signup(
        {
          email: formData.email,
          clinicName: formData.clinicName,
          leadProsthetist: formData.leadProsthetist,
        },
        formData.password,
      );
      setStep("done");
      setTimeout(() => {
        navigate("/ai-artifacts");
      }, 1500);
    } catch {
      setStep("form");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      {/* Header */}
      <header className="h-16 lg:h-20 flex items-center px-4 md:px-6 lg:px-8 border-b border-background-100 bg-white flex-shrink-0">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-heart-pulse-line text-base"></i>
          </span>
          <span className="font-heading text-sm lg:text-base font-bold text-foreground-950 whitespace-nowrap">
            Quick Prosthetics
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-100 text-[10px] font-semibold text-foreground-700">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-lock-line text-[9px]"></i>
            </span>
            Secure Clinical Backend
          </span>
          <Link
            to="/inbound-alert"
            className="text-[11px] md:text-xs font-semibold text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            ← Back
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {/* Page Header */}
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              Partnership Setup
            </span>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              Create Your Clinic Account &amp; Activate Partnership
            </h1>
            <p className="text-sm text-foreground-500 mt-2 max-w-2xl leading-relaxed">
              Complete your clinic profile, digitally sign the BAA, and process
              the one-time data lease fee to unlock the full patient clinical
              file — including PHI, contact details, 3D scan imagery, and
              prescribing physician connection.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* LEFT — Account & BAA */}
            <div className="flex-1 space-y-5">
              {/* Clinic Information */}
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-building-4-line text-sm"></i>
                  </span>
                  <div>
                    <h2 className="text-sm font-bold text-foreground-900">
                      Clinic Information
                    </h2>
                    <p className="text-[10px] text-foreground-400">
                      Create your secure enterprise profile
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5">
                      Clinic Name
                    </label>
                    <input
                      type="text"
                      value={formData.clinicName}
                      onChange={(e) =>
                        handleInputChange("clinicName", e.target.value)
                      }
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="Enter clinic name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5">
                      Lead Prosthetist
                    </label>
                    <input
                      type="text"
                      value={formData.leadProsthetist}
                      onChange={(e) =>
                        handleInputChange("leadProsthetist", e.target.value)
                      }
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="Full name and credentials"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="clinic@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="(555) 000-0000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5">
                      Clinic Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="Street address, city, state, ZIP"
                    />
                  </div>
                </div>
              </div>

              {/* Account Credentials */}
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-key-2-line text-sm"></i>
                  </span>
                  <div>
                    <h2 className="text-sm font-bold text-foreground-900">
                      Account Credentials
                    </h2>
                    <p className="text-[10px] text-foreground-400">
                      Set your password to access the clinical portal
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5"
                      htmlFor="signup-password"
                    >
                      Password
                    </label>
                    <input
                      id="signup-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="Minimum 6 characters"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-1.5"
                      htmlFor="signup-confirm-password"
                    >
                      Confirm Password
                    </label>
                    <input
                      id="signup-confirm-password"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      className={`w-full px-3.5 py-2.5 rounded-lg border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all ${
                        passwordError
                          ? "border-red-300"
                          : "border-background-200"
                      }`}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                    />
                    {passwordError && (
                      <p className="text-[10px] text-red-500 mt-1.5 flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-error-warning-line text-[10px]"></i>
                        </span>
                        {passwordError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* BAA Digital Signature */}
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-file-shield-2-line text-sm"></i>
                  </span>
                  <div>
                    <h2 className="text-sm font-bold text-foreground-900">
                      Business Associate Agreement
                    </h2>
                    <p className="text-[10px] text-foreground-400">
                      Required HIPAA compliance documentation
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50/70 p-4 max-h-48 overflow-y-auto mb-4">
                  <p className="text-[10px] text-foreground-600 leading-relaxed mb-3">
                    <strong className="text-foreground-800">
                      BUSINESS ASSOCIATE AGREEMENT
                    </strong>
                    <br />
                    This Business Associate Agreement (&quot;BAA&quot;) is
                    entered into by and between Quick Prosthetics Clinical
                    Network (&quot;Covered Entity&quot;) and the subscribing
                    clinic (&quot;Business Associate&quot;), effective as of the
                    date of digital execution below.
                  </p>
                  <p className="text-[10px] text-foreground-600 leading-relaxed mb-3">
                    <strong className="text-foreground-800">
                      1. Permitted Uses and Disclosures.
                    </strong>{" "}
                    Business Associate may use and disclose Protected Health
                    Information (PHI) solely for the purpose of prosthetic
                    patient care coordination, clinical artifact review,
                    prosthetic fabrication workflow management, and insurance
                    claims processing as authorized by the patient through
                    documented consent.
                  </p>
                  <p className="text-[10px] text-foreground-600 leading-relaxed mb-3">
                    <strong className="text-foreground-800">
                      2. Safeguards.
                    </strong>{" "}
                    Business Associate agrees to implement appropriate
                    administrative, physical, and technical safeguards to
                    prevent unauthorized use or disclosure of PHI, consistent
                    with 45 CFR §§ 164.308, 164.310, and 164.312.
                  </p>
                  <p className="text-[10px] text-foreground-600 leading-relaxed mb-3">
                    <strong className="text-foreground-800">
                      3. Data Lease Terms.
                    </strong>{" "}
                    Upon execution of this BAA and processing of the one-time
                    data lease fee, Covered Entity will provide Business
                    Associate with full access to the patient clinical file,
                    including PHI, contact details, 3D limb scan imagery,
                    medical evaluation, and prescribing physician connection for
                    dual sign-off.
                  </p>
                  <p className="text-[10px] text-foreground-600 leading-relaxed">
                    <strong className="text-foreground-800">
                      4. Term and Termination.
                    </strong>{" "}
                    This Agreement remains in effect for the duration of the
                    patient care episode. Either party may terminate upon
                    written notice if the other party breaches a material term.
                    Upon termination, Business Associate shall return or destroy
                    all PHI received from Covered Entity.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setBaaSigned(!baaSigned)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    baaSigned
                      ? "border-green-300 bg-green-50"
                      : "border-background-200 bg-background-50 hover:border-accent-200 hover:bg-accent-50/50"
                  }`}
                >
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${
                      baaSigned
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-foreground-300 text-transparent"
                    }`}
                  >
                    <i className="ri-check-line text-[10px]"></i>
                  </span>
                  <div className="text-left flex-1">
                    <p className="text-xs font-semibold text-foreground-800">
                      I have read and agree to the Business Associate Agreement
                    </p>
                    <p className="text-[10px] text-foreground-400">
                      By checking this box, you digitally sign this BAA on
                      behalf of {formData.clinicName || "your clinic"}
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* RIGHT — Payment Summary */}
            <div className="lg:w-[380px] flex-shrink-0 space-y-5">
              {/* Order Summary */}
              <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 sticky top-6">
                <h2 className="text-sm font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100">
                    <i className="ri-shopping-cart-2-line text-[10px] text-accent-600"></i>
                  </span>
                  Order Summary
                </h2>

                <div className="rounded-lg border border-background-200 bg-background-50/60 p-4 space-y-3 mb-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-foreground-800">
                        Quick Prosthetics Data Lease Fee
                      </p>
                      <p className="text-[10px] text-foreground-400">
                        One-time sourcing — full clinical file, artifacts,
                        physician connection
                      </p>
                    </div>
                    <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">
                      ${financialROI.dataLeaseFee.toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t border-background-100 pt-3 flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground-900">
                      Total Due
                    </span>
                    <span className="text-base font-bold text-foreground-950">
                      ${financialROI.dataLeaseFee.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* What's Included */}
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 mb-5">
                  <p className="text-[10px] font-semibold text-green-800 mb-2">
                    Upon activation, you will receive:
                  </p>
                  <ul className="space-y-1.5">
                    {[
                      "Complete patient clinical profile with full PHI",
                      "3D limb scan & residual limb photography",
                      "Physician medical evaluation & clinical notes",
                      "Insurance verification & L-Code reimbursement schedule",
                      "AI-generated Standard Work Order (SWO)",
                      "Direct physician connection for dual sign-off",
                      "HIPAA-compliant audit trail logging",
                    ].map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[10px] text-green-700 leading-relaxed"
                      >
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[8px] text-green-500"></i>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Payment Method */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold mb-3">
                    Payment Method
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[9px] text-foreground-500 font-medium mb-1">
                        Card Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={cardData.number}
                          onChange={(e) =>
                            handleCardChange("number", e.target.value)
                          }
                          placeholder="•••• •••• •••• ••••"
                          maxLength={19}
                          className="w-full px-3.5 py-2.5 pl-9 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all font-mono tracking-wider"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
                          <i className="ri-bank-card-line text-xs"></i>
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[9px] text-foreground-500 font-medium mb-1">
                          Expiry
                        </label>
                        <input
                          type="text"
                          value={cardData.expiry}
                          onChange={(e) =>
                            handleCardChange("expiry", e.target.value)
                          }
                          placeholder="MM / YY"
                          maxLength={7}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] text-foreground-500 font-medium mb-1">
                          CVC
                        </label>
                        <input
                          type="text"
                          value={cardData.cvc}
                          onChange={(e) =>
                            handleCardChange("cvc", e.target.value)
                          }
                          placeholder="•••"
                          maxLength={4}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[9px] text-foreground-500 font-medium mb-1">
                        Name on Card
                      </label>
                      <input
                        type="text"
                        value={cardData.name}
                        onChange={(e) =>
                          handleCardChange("name", e.target.value)
                        }
                        placeholder="Full name as it appears on card"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Secure payment notice */}
                <div className="flex items-center gap-2 mt-4 mb-5 text-foreground-400">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-[10px]"></i>
                  </span>
                  <p className="text-[9px] leading-relaxed">
                    Payment processed securely via Stripe. Your card information
                    is encrypted and never stored on our servers.
                  </p>
                </div>

                {/* Submit Button */}
                {step === "form" && (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                      canSubmit
                        ? "bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
                        : "bg-foreground-100 text-foreground-400 cursor-not-allowed"
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-lock-unlock-line text-base"></i>
                    </span>
                    Complete Setup &amp; Unlock Patient Files
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-base"></i>
                    </span>
                  </button>
                )}

                {step === "processing" && (
                  <div className="w-full flex flex-col items-center justify-center gap-3 px-6 py-6 rounded-xl bg-accent-50 border border-accent-200 animate-fadeSlideIn">
                    <span className="w-8 h-8 flex items-center justify-center text-accent-600">
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                    </span>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-accent-700">
                        Creating your account...
                      </p>
                      <p className="text-[10px] text-accent-500 mt-1">
                        Processing payment and activating partnership
                      </p>
                    </div>
                  </div>
                )}

                {step === "done" && (
                  <div className="w-full flex flex-col items-center justify-center gap-3 px-6 py-6 rounded-xl bg-green-50 border border-green-200 animate-fadeSlideIn">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                      <i className="ri-check-double-line text-lg"></i>
                    </span>
                    <div className="text-center">
                      <p className="text-sm font-bold text-green-800">
                        Account Created &amp; Partnership Activated!
                      </p>
                      <p className="text-[10px] text-green-600 mt-1">
                        Redirecting to clinical backend...
                      </p>
                    </div>
                  </div>
                )}

                {(step === "processing" || step === "done") && (
                  <div className="mt-4 pt-4 border-t border-background-100">
                    <p className="text-[9px] text-foreground-400 text-center">
                      You will be redirected automatically
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
